<template>
  <!-- AI F §1.5.1 — question patterns, keyword rules and the response (up to 5000 characters) -->
  <div class="p-8">
    <woot-modal-header
      :header-title="
        source
          ? $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.EDIT_TITLE')
          : $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.ADD_TITLE')
      "
      :header-content="$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.DESC')"
    />
    <form class="mt-4" @submit.prevent="submit">
      <woot-input
        v-model.trim="name"
        :label="$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.NAME.LABEL')"
        :placeholder="$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.NAME.PLACEHOLDER')"
      />

      <label>
        {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.QUESTIONS.LABEL') }}
        <textarea
          v-model="questions"
          rows="3"
          :placeholder="
            $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.QUESTIONS.PLACEHOLDER')
          "
        />
        <span class="text-xs text-slate-500">
          {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.QUESTIONS.HINT') }}
        </span>
      </label>

      <label class="mt-3 block">
        {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.KEYWORDS.LABEL') }}
        <textarea
          v-model="keywords"
          rows="2"
          :placeholder="$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.KEYWORDS.PLACEHOLDER')"
        />
        <span class="text-xs text-slate-500">
          {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.KEYWORDS.HINT') }}
        </span>
      </label>

      <div class="mt-3">
        <span class="block">
          {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.ANSWER.LABEL') }}
        </span>
        <!-- AI F §1.5.1 — the editor writes Markdown, the same the panel and "Test query" render -->
        <woot-message-editor
          v-model="answer"
          :enabled-menu-options="menuOptions"
          :split-list-item-on-enter="true"
          :enable-suggestions="false"
          :enable-canned-responses="false"
          :placeholder="$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.ANSWER.PLACEHOLDER')"
        />
        <!-- AI F §1.5.1 — the editor has no maxlength, so the count is what warns about the 5000 limit -->
        <span
          class="block text-xs ltr:text-right rtl:text-left"
          :class="isTooLong ? 'text-red-700' : 'text-slate-500'"
        >
          {{ answer.length }}/{{ maxChars }}
        </span>
      </div>

      <div class="flex justify-end gap-2 mt-4">
        <woot-button variant="clear" @click.prevent="$emit('close')">
          {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSaving"
          :is-disabled="!isValid || isSaving"
        >
          {{ $t('KNOWLEDGE_BASE.CUSTOM_ANSWER.SAVE') }}
        </woot-button>
      </div>
    </form>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { MESSAGE_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';

const MAX_ANSWER_CHARS = 5000;
const MENU_OPTIONS = MESSAGE_EDITOR_MENU_OPTIONS;

export default {
  components: { WootMessageEditor },
  mixins: [alertMixin],
  props: {
    source: {
      type: Object,
      default: null,
    },
  },
  data() {
    const settings = this.source?.settings || {};
    return {
      name: this.source?.name || '',
      questions: (settings.questions || []).join('\n'),
      keywords: (settings.keywords || []).join('\n'),
      answer: settings.answer || '',
      isSaving: false,
      maxChars: MAX_ANSWER_CHARS,
      menuOptions: MENU_OPTIONS,
    };
  },
  computed: {
    isTooLong() {
      return this.answer.length > this.maxChars;
    },
    isValid() {
      return (
        this.name.trim() &&
        this.answer.trim() &&
        !this.isTooLong &&
        (this.lines(this.questions).length || this.lines(this.keywords).length)
      );
    },
  },
  methods: {
    lines(value) {
      return value
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
    },
    async submit() {
      this.isSaving = true;
      const payload = {
        name: this.name.trim(),
        source_type: 'custom_answer',
        settings: {
          questions: this.lines(this.questions),
          keywords: this.lines(this.keywords),
          answer: this.answer.trim(),
        },
      };
      try {
        await this.$store.dispatch(
          this.source ? 'kbSources/update' : 'kbSources/create',
          this.source ? { id: this.source.id, ...payload } : payload
        );
        this.showAlert(this.$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.SAVED'));
        this.$emit('saved');
      } catch (error) {
        this.showAlert(
          error?.response?.data?.error ||
            this.$t('KNOWLEDGE_BASE.CUSTOM_ANSWER.ERROR')
        );
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-woot-style {
    @apply min-h-[9rem] max-h-80 overflow-auto;
  }
}
</style>
